/**
 * TOSS Web Application - Notifications API Service
 *
 * API functions for notification endpoints:
 * - Get notifications list (paginated)
 * - Get unread count
 * - Mark notification as read
 * - Mark all notifications as read
 */

import apiClient from './client';
import {
  API_ENDPOINTS,
  PaginatedResponse,
  SuccessResponse,
} from '@/types';
import type {
  Notification,
  NotificationFilters,
  UnreadNotificationsCount,
} from '@/types';

// ============================================================================
// Types
// ============================================================================

/**
 * Notification list request parameters
 * Index signature allows assignment to Record<string, unknown>
 */
export interface NotificationListRequest {
  /** Page number (1-based) */
  page?: number;
  /** Page size */
  pageSize?: number;
  /** Filter by notification types */
  types?: string[];
  /** Filter by read status */
  isRead?: boolean;
  /** Filter by priority sector (true = only priority, false = only the rest) */
  isPriority?: boolean;
  /** Filter by date range start */
  dateFrom?: string;
  /** Filter by date range end */
  dateTo?: string;
  [key: string]: unknown;
}

/**
 * Notification list response
 */
export type NotificationListResponse = PaginatedResponse<Notification>;

// ============================================================================
// Notifications API Functions
// ============================================================================

/**
 * Get paginated list of notifications
 *
 * @param params - Filter and pagination parameters
 * @returns Paginated notifications list
 *
 * @example
 * ```ts
 * const notifications = await notificationsApi.getNotifications({
 *   page: 1,
 *   pageSize: 20,
 *   isRead: false
 * });
 * ```
 */
export async function getNotifications(
  params?: NotificationListRequest
): Promise<NotificationListResponse> {
  const response = await apiClient.get<NotificationListResponse>(
    API_ENDPOINTS.NOTIFICATIONS.BASE,
    { params }
  );
  return response.data;
}

/**
 * Get unread notifications count
 *
 * @returns Unread count
 *
 * @example
 * ```ts
 * const { count } = await notificationsApi.getUnreadCount();
 * ```
 */
export async function getUnreadCount(): Promise<UnreadNotificationsCount> {
  const response = await apiClient.get<UnreadNotificationsCount>(
    API_ENDPOINTS.NOTIFICATIONS.UNREAD_COUNT
  );
  return response.data;
}

/**
 * Mark a single notification as read
 *
 * @param id - Notification ID
 * @returns Success response
 *
 * @example
 * ```ts
 * await notificationsApi.markAsRead('notification-id');
 * ```
 */
export async function markAsRead(id: string): Promise<SuccessResponse> {
  const response = await apiClient.patch<SuccessResponse>(
    API_ENDPOINTS.NOTIFICATIONS.READ(id)
  );
  return response.data;
}

/**
 * Mark all notifications as read
 *
 * @returns Success response
 *
 * @example
 * ```ts
 * await notificationsApi.markAllAsRead();
 * ```
 */
export async function markAllAsRead(): Promise<SuccessResponse> {
  const response = await apiClient.patch<SuccessResponse>(
    API_ENDPOINTS.NOTIFICATIONS.READ_ALL
  );
  return response.data;
}

// ============================================================================
// Namespace Export
// ============================================================================

/**
 * Notifications API namespace for convenient imports
 *
 * @example
 * ```ts
 * import { notificationsApi } from '@/api/notifications.api';
 *
 * const notifications = await notificationsApi.getNotifications();
 * const { count } = await notificationsApi.getUnreadCount();
 * await notificationsApi.markAsRead(id);
 * ```
 */
export const notificationsApi = {
  getNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
} as const;

export default notificationsApi;
